import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconChevronDown, IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import { cn } from '@/utils/cn';
import { TeeCheckbox } from '@/components/ui/tee-checkbox';

export interface TeeMultiSelectOption {
  label: string;
  value: string;
}

export interface TeeMultiSelectProps {
  options: TeeMultiSelectOption[];
  values: string[];
  onChange: (values: string[]) => void;
  label?: string;
  hint?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  allowCreate?: boolean;
  creating?: boolean;
  onCreateOption?: (label: string) => void;
  createOptionLabel?: (term: string) => string;
}

export const TeeMultiSelect: React.FC<TeeMultiSelectProps> = ({
  options,
  values,
  onChange,
  label,
  hint,
  placeholder = 'Select',
  searchPlaceholder = 'Search',
  disabled = false,
  className,
  allowCreate = false,
  creating = false,
  onCreateOption,
  createOptionLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const trimmedSearchTerm = searchTerm.trim();
  const hasExactMatch = options.some(
    (option) => option.label.trim().toLowerCase() === trimmedSearchTerm.toLowerCase()
  );
  const showCreateRow = allowCreate && trimmedSearchTerm.length > 0 && !hasExactMatch;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setHighlightedIndex(0);
    } else {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  const openMenu = () => {
    if (disabled) return;
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    }
    setIsOpen((prev) => !prev);
  };

  const toggleValue = (value: string) => {
    if (values.includes(value)) {
      onChange(values.filter((v) => v !== value));
    } else {
      const nextSet = new Set([...values, value]);
      onChange(options.filter((o) => nextSet.has(o.value)).map((o) => o.value));
    }
  };

  const removeValue = (value: string) => {
    onChange(values.filter((v) => v !== value));
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
      case ' ':
        if (document.activeElement === searchInputRef.current && e.key === ' ') {
          break;
        }
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          toggleValue(filteredOptions[highlightedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      default:
        break;
    }
  };

  const selectedOptions = options.filter((o) => values.includes(o.value));
  const visibleChips = selectedOptions.slice(0, 2);
  const extraCount = selectedOptions.length - visibleChips.length;

  const triggerLabel =
    selectedOptions.length === 0
      ? placeholder
      : `${visibleChips.map((o) => o.label).join(', ')}${
          extraCount > 0 ? ` +${extraCount}` : ''
        }`;

  const labelClasses = 'text-body-s font-medium text-[var(--text-content-title)]';
  const hintClasses = 'text-body-xs font-medium text-[var(--text-content-sub-title)]';

  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      {label && <label className={labelClasses}>{label}</label>}

      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={openMenu}
          disabled={disabled}
          className={cn(
            'w-full flex items-center justify-between px-[16px] py-[8px] rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card)] text-left text-body-s text-[var(--text-content-title)] transition-colors',
            'hover:bg-[var(--background-card-hover)] focus:outline-none focus:ring-1 focus:ring-[var(--border-focus)]',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <span className="flex-1 truncate">{triggerLabel}</span>
          <IconChevronDown
            size={16}
            className={cn(
              'text-[var(--text-content-sub-title)] transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {isOpen &&
          !disabled &&
          createPortal(
            <div
              ref={menuRef}
              onKeyDown={handleMenuKeyDown}
              className="fixed z-50 rounded-[12px] border border-[var(--border-default)] bg-[var(--background-card)] py-1 shadow-[0px_8px_32px_0px_rgba(0,0,0,0.15)]"
              style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                width: `${menuPosition.width}px`,
              }}
            >
              <div className="flex items-center gap-2 border-b border-[var(--border-default)] px-3 py-2">
                <IconSearch
                  size={16}
                  className="text-[var(--text-content-sub-title)]"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full bg-transparent text-body-s text-[var(--text-content-title)] outline-none placeholder:text-[var(--text-content-sub-title)]"
                />
              </div>

              <div className="max-h-64 overflow-y-auto">
                {filteredOptions.length === 0 && !showCreateRow && (
                  <p className="px-3 py-2 text-body-s text-[var(--text-content-sub-title)]">
                    No matches
                  </p>
                )}
                {filteredOptions.map((option, index) => (
                  <div
                    key={option.value}
                    onClick={() => toggleValue(option.value)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 px-3 py-2 transition-colors',
                      'hover:bg-[var(--background-card-hover)]',
                      highlightedIndex === index &&
                        'bg-[var(--background-card-hover)]'
                    )}
                  >
                    <TeeCheckbox
                      checked={values.includes(option.value)}
                      onChange={() => toggleValue(option.value)}
                      label={option.label}
                    />
                  </div>
                ))}
                {showCreateRow && (
                  <div
                    onClick={() => {
                      if (creating) return;
                      onCreateOption?.(trimmedSearchTerm);
                    }}
                    aria-disabled={creating}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 text-body-s text-[var(--color-forest)] transition-colors',
                      creating ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-[var(--background-card-hover)]'
                    )}
                  >
                    <IconPlus size={14} />
                    <span className="truncate">
                      {createOptionLabel
                        ? createOptionLabel(trimmedSearchTerm)
                        : `Create "${trimmedSearchTerm}"`}
                    </span>
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>

      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedOptions.map((option) => (
            <span
              key={option.value}
              className="flex items-center gap-1 rounded-[6px] bg-[var(--background-badge-gray)] px-2 py-1 text-[14px] text-[var(--text-badge-gray)]"
            >
              {option.label}
              <button
                type="button"
                aria-label={`Remove ${option.label}`}
                onClick={() => removeValue(option.value)}
                disabled={disabled}
                className="flex items-center"
              >
                <IconX size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {hint && <p className={hintClasses}>{hint}</p>}
    </div>
  );
};

TeeMultiSelect.displayName = 'TeeMultiSelect';

export default TeeMultiSelect;
